import { add } from "date-fns";

import { getAdminToken, getToken } from "auth/helpers/authApi";

import { apiClient } from "utils/apiClient";

import { JourneyActivity, JourneyActivityType } from "./employee-journey.types";
import { IEmployeeJourneyFilter } from "./employee-journey-filter";
import { DailyMission } from "utils/validations/daily-mission";

export const getEmployeeJourneys = async (params?: IEmployeeJourneyFilter) => {
	const token = getAdminToken();
	const response = await apiClient("admin/journey/journey-templates", {
		token,
		params,
	});

	return {
		data: {
			data: response.data,
			totalDataFound: response.data.length,
		},
	};
};

export const getEmployeeJourneyTemplates = async () => {
	const token = getAdminToken();
	const response = await apiClient("admin/journey/journey-templates/templates", { token });

	return response.data;
};

export const createJourneyByTemplate = async (journeyTemplateId: string) => {
	const token = getAdminToken();
	const response = await apiClient(`admin/journey/journey-templates/${journeyTemplateId}/duplicate`, {
		token,
		method: "POST",
	});

	return response;
};

export const getCustomVariables = async () => {
	const token = getAdminToken();
	try {
		const response = await apiClient("admin/journey/custom-variables", { token });
		return response.data;
	} catch (error) {
		console.log("error getCustomVariables", error);
	}
};

export const getCustomRecipients = async () => {
	const token = getAdminToken();
	try {
		const response = await apiClient("admin/custom-recipients", { token });
		return response.data;
	} catch (error) {
		console.log("error getCustomRecipients", error);
	}
};

export const getDailyMission = async (journeyTemplateId: string) => {
	const token = getAdminToken();
	try {
		const response = await apiClient<DailyMission[]>(
			`admin/journey/journey-templates/${journeyTemplateId}/daily-missions`,
			{
				token,
			},
		);
		return response;
	} catch (error) {
		console.log("error getDailyMission", error);
	}
};

export const getUserDailyMission = async () => {
	const token = getToken();
	try {
		const response = await apiClient("daily-mission/current", {
			token,
		});
		return response;
	} catch (error) {
		console.log("error getUserDailyMission", error);
	}
};

export const completeUserDailyMission = async (userDailyMissionId: string, pin: string) => {
	const token = getToken();
	try {
		const response = await apiClient(`daily-mission/${userDailyMissionId}/complete`, {
			token,
			data: { pin },
		});
		return response;
	} catch (error) {
		console.log("error completeUserDailyMission", error);
		throw error;
	}
};

export const getDailyMissionTemplate = async (journeyTemplateId: string) => {
	const token = getAdminToken();
	try {
		const response = await apiClient<DailyMission[]>(
			`admin/journey/journey-templates/${journeyTemplateId}/daily-missions/template`,
			{
				token,
			},
		);
		return response;
	} catch (error) {
		console.log("error getDailyMissionTemplate", error);
	}
};

export const saveDailyMission = async (journeyTemplateId: string, data: DailyMission) => {
	const token = getAdminToken();
	try {
		const url = !!data?.["_id"]
			? `admin/journey/journey-templates/daily-missions/${data["_id"]}`
			: `admin/journey/journey-templates/${journeyTemplateId}/daily-missions`;

		const response = await apiClient(url, {
			token,
			data,
			method: !!data?.["_id"] ? "PUT" : "POST",
		});

		return response;
	} catch (error) {
		console.log("error saveDailyMission", error);
		throw error;
	}
};

export const deleteDailyMission = async (missionId: string) => {
	const token = getAdminToken();
	try {
		const response = await apiClient(`admin/journey/journey-templates/daily-missions/${missionId}`, {
			token,
			method: "DELETE",
		});
		return response;
	} catch (error) {
		console.log("error deleteDailyMission", error);
	}
};

export const parseActivityPayload = (data: JourneyActivity) => {
	const formData = new FormData();

	let parsedData = {};

	if (data.type === JourneyActivityType.STORY) {
		parsedData = {
			name: data.name,
			dependencies: data.dependencies,
			description: data.description,
			journeyRouteTemplateId: data.routeId,
			type: data.type,
			dueRelativeDateTemplate: data.dueRelativeDateTemplate,
			lockedUntilRelativeDateTemplate: data.lockedUntilRelativeDateTemplate,
			ruleset: {
				roomTemplateId: data.roomTemplateId,
				maxNoUsers: Number(data.maxNoUsers),
				type: data.type,
			},
		};
	} else if (data.type === JourneyActivityType.UPLOAD_FILE) {
		parsedData = {
			name: data.name,
			dependencies: data.dependencies,
			description: data.description,
			journeyRouteTemplateId: data.routeId,
			type: data.type,
			dueRelativeDateTemplate: data.dueRelativeDateTemplate,
			lockedUntilRelativeDateTemplate: data.lockedUntilRelativeDateTemplate,
			ruleset: {
				filePath: data.filePath,
				fileType: data.fileType,
				file: data.file,
				type: data.type,
			},
		};
	} else if (data.type === JourneyActivityType.MEETING) {
		parsedData = {
			name: data.name,
			dependencies: data.dependencies,
			description: data.description,
			journeyRouteTemplateId: data.routeId,
			type: data.type,
			dueRelativeDateTemplate: data.dueRelativeDateTemplate,
			lockedUntilRelativeDateTemplate: data.lockedUntilRelativeDateTemplate,
			ruleset: {
				participants: data.participants,
				startTime: data.startTime,
				endTime: data.endTime,
				type: data.type,
			},
		};
	} else if (data.type === JourneyActivityType.COMMUNICATION) {
		parsedData = {
			dependencies: data.dependencies,
			journeyRouteTemplateId: data.routeId,
			type: data.type,
			dueRelativeDateTemplate: data.dueRelativeDateTemplate,
			lockedUntilRelativeDateTemplate: data.lockedUntilRelativeDateTemplate,
			ruleset: {
				recipients: data.recipients,
				subject: data.subject,
				message: data.message,
				placeholder: data.placeholder,
				time: data.time,
				dueRelativeDateTemplate: data.dueRelativeDateTemplate,
				lockedUntilRelativeDateTemplate: data.lockedUntilRelativeDateTemplate,
				communicationType: data.communicationType.toLowerCase(),
				type: data.type,
			},
		};
	} else if (data.type === JourneyActivityType.SETUP_GOAL) {
		parsedData = {
			journeyRouteTemplateId: data.routeId,
			name: data.name,
			description: data.description,
			dueRelativeDateTemplate: data.dueRelativeDateTemplate,
			lockedUntilRelativeDateTemplate: data.lockedUntilRelativeDateTemplate,
			type: data.type,
			dependencies: data.dependencies || [],
			ruleset: {
				needApproval: data.needApproval,
				type: data.type,
			},
		};
		return parsedData;
	} else if (data.type === JourneyActivityType.QUESTION) {
		parsedData = {
			journeyRouteTemplateId: data.routeId,
			type: data.type,
			dueRelativeDateTemplate: data.dueRelativeDateTemplate,
			name: data.name,
			sendEmail: data.sendEmail,
			ruleset: {
				questionnaireId: data.questionnaireId["_id"],
				type: data.type,
				sendEmail: data.sendEmail,
				recipients: data.recipients,
			},
		};
		return parsedData;
	}

	Object.keys(parsedData).forEach((key) => {
		if (key === "ruleset") {
			Object.keys(parsedData[key]).forEach((rulesetKey) => {
				if (parsedData[key][rulesetKey] === undefined || parsedData[key][rulesetKey] === null) {
					return;
				}
				if (rulesetKey === "participants" || rulesetKey === "recipients") {
					parsedData[key][rulesetKey].forEach((participant: any, index: number) => {
						formData.append(`ruleset[${rulesetKey}][${index}][name]`, participant.name);
						formData.append(`ruleset[${rulesetKey}][${index}][email]`, participant.email);

						// Use recipientsType for Communication Activity's Recipients, otherwise use participantType (Meeting Activity)
						const participantTypeKey = rulesetKey === "recipients" ? "recipientsType" : "participantType";

						formData.append(`ruleset[${rulesetKey}][${index}][recipientId]`, participant.recipientId);
						formData.append(
							`ruleset[${rulesetKey}][${index}][${participantTypeKey}]`,
							participant.participantType.toLowerCase(),
						);
					});
					return;
				}
				formData.append(`ruleset[${rulesetKey}]`, parsedData[key][rulesetKey]);
			});
		} else if (key === "dependencies") {
			parsedData[key].forEach((dependency: any, index: number) => {
				formData.append(`dependencies[${index}]`, dependency);
			});
			return;
		} else {
			formData.append(key, parsedData[key]);
		}
	});

	return formData;
};

export const parseResponseActivity = (route: any, activity: any) => {
	if (activity.element.type === JourneyActivityType.STORY) {
		return {
			_id: activity.element._id,
			name: activity.element.ruleset?.roomTemplateId?.name,
			type: activity.element.type,
			description: activity.element.description,
			date: add(new Date(), { days: Number(activity.dueRelativeDateTemplate.split(" ")[0]) }),
			routeId: route._id,
			maxNoUsers: activity.element.ruleset.maxNoUsers,
			duration: activity.element.ruleset?.roomTemplateId?.maxDurationMS / 1000,
			roomTemplateId: activity.element.ruleset?.roomTemplateId?._id,
			dueRelativeDateTemplate: activity.dueRelativeDateTemplate,
			lockedUntilRelativeDateTemplate: activity.lockedUntilRelativeDateTemplate,
			dependencies: activity.dependencies?.map((d) => d.element) ?? [],
		};
	}
	if (activity.element.type === JourneyActivityType.UPLOAD_FILE) {
		return {
			_id: activity.element._id,
			name: activity.element.name,
			type: activity.element.type,
			description: activity.element.description,
			date: add(new Date(), { days: Number(activity.dueRelativeDateTemplate.split(" ")[0]) }),
			routeId: route._id,
			filePath: activity.element.ruleset.filePath,
			fileType: activity.element.ruleset.fileType,
			roomTemplateId: activity.element.roomTemplateId?._id,
			dueRelativeDateTemplate: activity.dueRelativeDateTemplate ?? "",
			lockedUntilRelativeDateTemplate: activity.lockedUntilRelativeDateTemplate ?? "",
			dependencies: activity.dependencies?.map((d) => d.element) ?? [],
		};
	}

	if (activity.element.type === JourneyActivityType.MEETING) {
		return {
			_id: activity.element._id,
			name: activity.element.name,
			type: activity.element.type,
			description: activity.element.description,
			date: add(new Date(), { days: Number(activity.dueRelativeDateTemplate.split(" ")[0]) }),
			eventTime: activity.element.ruleset.startTime + " - " + activity.element.ruleset.endTime,
			routeId: route._id,
			startTime: activity.element.ruleset.startTime,
			endTime: activity.element.ruleset.endTime,
			participants: activity.element.ruleset.participants,
			roomTemplateId: activity.element.roomTemplateId?._id,
			dueRelativeDateTemplate: activity.dueRelativeDateTemplate ?? "",
			lockedUntilRelativeDateTemplate: activity.lockedUntilRelativeDateTemplate ?? "",
			dependencies: activity.dependencies?.map((d) => d.element) ?? [],
		};
	}

	if (activity.element.type === JourneyActivityType.COMMUNICATION) {
		return {
			_id: activity.element._id,
			routeId: route._id,
			type: activity.element.type,
			time: activity.element.ruleset.time,
			message: activity.element.ruleset.message,
			placeholder: activity.element.ruleset.placeholder,
			subject: activity.element.ruleset.subject,
			date: add(new Date(), { days: Number(activity.dueRelativeDateTemplate.split(" ")[0]) }),
			dueRelativeDateTemplate: activity.element.ruleset.dueRelativeDateTemplate,
			recipients: activity.element.ruleset.recipients,
			communicationType: activity.element.ruleset.communicationType,
		};
	}

	if (activity.element.type === JourneyActivityType.SETUP_GOAL) {
		return {
			_id: activity.element._id,
			routeId: route._id,
			type: activity.element.type,
			name: activity.element.name,
			date: add(new Date(), { days: Number(activity.dueRelativeDateTemplate.split(" ")[0]) }),
			description: activity.element.description,
			needApproval: activity.element.ruleset.needApproval,
			dueRelativeDateTemplate: activity.dueRelativeDateTemplate,
			lockedUntilRelativeDateTemplate: activity.lockedUntilRelativeDateTemplate,
			dependencies: activity.dependencies?.map((d) => d.element) ?? [],
		};
	}

	if (activity.element.type === JourneyActivityType.QUESTION) {
		return {
			_id: activity.element._id,
			routeId: route._id,
			type: activity.element.type,
			name: activity.element.name,
			dueRelativeDateTemplate: activity.dueRelativeDateTemplate,
			lockedUntilRelativeDateTemplate: activity.lockedUntilRelativeDateTemplate,
			sendEmail: activity.element.ruleset.sendEmail,
			questionnaireId: activity.element.ruleset.questionnaireId,
			recipients: activity.element.ruleset.recipients,
			dependencies: activity.dependencies?.map((d) => d.element) ?? [],
		};
	}

	if (activity.element.type === JourneyActivityType.TASK) {
		return {
			_id: activity.element._id,
			routeId: route._id,
			type: activity.element.type,
			name: activity.element.name,
			description: activity.element.description,
			placeholder: activity.element.ruleset.placeholder,
			dueRelativeDateTemplate: activity.dueRelativeDateTemplate,
			taskTime: activity.element.ruleset.taskTime,
			taskDate: activity.element.ruleset.taskDate,
			recipients: activity.element.ruleset.recipients,
			dependencies: activity.dependencies?.map((d) => d.element) ?? [],
			user: activity,
		};
	}

	return {};
};

export const createActivity = async (data: JourneyActivity) => {
	const token = getAdminToken();

	const response = await apiClient("admin/journey/journey-route-templates/activities", {
		token,
		formData: parseActivityPayload(data),
	});

	const json = await response.json();

	return parseResponseEmployeeJourney(json.data);
};

export const updateActivity = async (id: string, data: JourneyActivity) => {
	const token = getAdminToken();
	const response = await apiClient(`admin/journey/journey-route-templates/activities/${id}`, {
		token,
		...([JourneyActivityType.SETUP_GOAL, JourneyActivityType.QUESTION].includes(data.type)
			? {
					data: parseActivityPayload(data),
			  }
			: { formData: parseActivityPayload(data) }),
		method: "PUT",
	});

	if ([JourneyActivityType.SETUP_GOAL, JourneyActivityType.QUESTION].includes(data.type)) {
		return response;
	}

	const json = await response.json();
	return parseResponseEmployeeJourney(json.data);
};

export const deleteActivity = async (id: string) => {
	const token = getAdminToken();
	const response = await apiClient(`admin/journey/journey-route-templates/activities/${id}`, {
		token,
		method: "DELETE",
	});
	return response.data;
};

const parseResponseEmployeeJourney = (employeeJourney: any) => {
	return {
		...employeeJourney,
		routes: employeeJourney.routesIds?.map((route) => ({
			...route,
			id: route._id,
			type: "route",
			activities: route.elements.map((activity) => parseResponseActivity(route, activity)),
		})),
	};
};

export const getEmployeeJourney = async (id: string) => {
	const token = getAdminToken();
	const response = await apiClient(`admin/journey/journey-templates/${id}`, {
		token,
	});
	return parseResponseEmployeeJourney(response.data);
};

export const createJourneyTemplate = async (data: {}) => {
	const token = getAdminToken();
	const response = await apiClient("admin/journey/journey-templates", {
		token,
		data,
	});
	return response.data;
};

export const updateJourneyTemplate = async (id: string, data: {}) => {
	const token = getAdminToken();
	const response = await apiClient(`admin/journey/journey-templates/${id}`, {
		token,
		data,
		method: "PUT",
	});
	return response.data;
};

export const updateJourneyTemplateRoutes = async (id: string, routeIds: string[]) => {
	const token = getAdminToken();
	const response = await apiClient(`admin/journey/journey-templates/${id}/routes`, {
		token,
		data: {
			routeIds,
		},
		method: "PUT",
	});
	return response.data;
};

export const archiveJourneyTemplate = async (employeeJourneyId: string) => {
	const token = getAdminToken();
	const response = await apiClient(`admin/journey/journey-templates/${employeeJourneyId}/archive`, {
		token,
		method: "POST",
	});
	return response.data;
};

export const publishJourneyTemplate = async (employeeJourneyId: string) => {
	const token = getAdminToken();
	const response = await apiClient(`admin/journey/journey-templates/${employeeJourneyId}/publish`, {
		token,
		method: "POST",
	});
	return response.data;
};

export const createJourneyRoute = async (data: {}) => {
	const token = getAdminToken();
	const formData = new FormData();
	Object.keys(data).forEach((key) => {
		if (key === "icon" && !!data[key]) {
			formData.append(key, data[key], data[key].name);
			return;
		}
		formData.append(key, data[key]);
	});

	const response = await apiClient("admin/journey/journey-route-templates", {
		token,
		formData,
	});

	const json = await response.json();

	return json.data;
};

export const updateJourneyRoute = async (id: string, data: {}) => {
	const token = getAdminToken();
	const formData = new FormData();
	Object.keys(data).forEach((key) => {
		if (key === "icon" && !!data[key]) {
			formData.append(key, data[key], data[key].name);
			return;
		}
		formData.append(key, data[key]);
	});
	const response = await apiClient(`admin/journey/journey-route-templates/${id}`, {
		token,
		formData,
		method: "PUT",
	});
	const json = await response.json();

	return json.data;
};

export const deleteJourneyRoute = async (id: string) => {
	const token = getAdminToken();
	const response = await apiClient(`admin/journey/journey-route-templates/${id}`, {
		token,
		method: "DELETE",
	});
	return response.data;
};

export const previewJourneyTemplate = async (id: string) => {
	const token = getAdminToken();
	const response = await apiClient(`admin/journey/journey-templates/${id}/preview`, {
		token,
		method: "POST",
	});

	return response.data;
};
